import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { z } from "zod";
import { PlannerErrorPage } from "@/components/planner-error-page";
import { TakeoffToolLayout } from "@/components/takeoff-app-sidebar";
import { TakeoffList } from "@/components/takeoff-list";
import { orpc } from "@/utils/orpc";

const projectIdSchema = z.uuid();

export const Route = createFileRoute("/project/$projectId/")({
	component: ProjectTakeoffsRoute,
});

function ProjectTakeoffsRoute(): ReactElement {
	const { projectId } = Route.useParams();
	const parsed = projectIdSchema.safeParse(projectId);
	if (!parsed.success) {
		return (
			<PlannerErrorPage
				description="The planner link is missing a valid project id."
				title="Invalid project link"
			/>
		);
	}
	return (
		<TakeoffToolLayout projectId={parsed.data}>
			<ProjectTakeoffList projectId={parsed.data} />
		</TakeoffToolLayout>
	);
}

const ProjectTakeoffList = ({
	projectId,
}: {
	projectId: string;
}): ReactElement => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const listQuery = useQuery(
		orpc.takeoffs.list.queryOptions({ input: { projectId } }),
	);
	// Soft-delete moves rows into the trash view — invalidate both list keys.
	const invalidateList = async (): Promise<void> => {
		await Promise.all([
			queryClient.invalidateQueries({
				queryKey: orpc.takeoffs.list.queryOptions({ input: { projectId } })
					.queryKey,
			}),
			queryClient.invalidateQueries({
				queryKey: orpc.takeoffs.list.queryOptions({
					input: { deleted: true, projectId },
				}).queryKey,
			}),
		]);
	};
	const createTakeoff = useMutation(
		orpc.takeoffs.create.mutationOptions({ onSuccess: invalidateList }),
	);
	const deleteTakeoff = useMutation(
		orpc.takeoffs.delete.mutationOptions({ onSuccess: invalidateList }),
	);

	// list() returns a union of the active projection and the metadata-only
	// trash projection. This route never passes `deleted`, so keep the rows that
	// carry the full active shape (fileCount/description/state/updatedAt).
	// Widen `A[] | B[]` to `(A | B)[]` first so the filter can narrow per-row.
	const listRows: Array<NonNullable<typeof listQuery.data>[number]> =
		listQuery.data ?? [];
	const activeRows = listRows.filter(
		(row): row is Extract<typeof row, { fileCount: number }> =>
			"fileCount" in row,
	);

	if (listQuery.isLoading) {
		return <p className="p-6">Loading Takeoffs…</p>;
	}
	if (listQuery.isError) {
		return (
			<PlannerErrorPage
				description="Takeoffs could not be loaded for this project."
				title="Takeoffs unavailable"
			/>
		);
	}

	return (
		<TakeoffList
			onCreate={(input) =>
				createTakeoff.mutate({
					description: input.description || undefined,
					discipline: input.discipline,
					name: input.name,
					projectId,
				})
			}
			onDelete={(takeoffId) => deleteTakeoff.mutate({ takeoffId })}
			onOpen={(takeoffId) =>
				void navigate({
					params: { projectId, takeoffId },
					to: "/project/$projectId/takeoff/$takeoffId",
				})
			}
			takeoffs={activeRows.map((takeoff) => ({
				accessible: true,
				description: takeoff.description ?? null,
				discipline: takeoff.discipline,
				fileCount: takeoff.fileCount,
				id: takeoff.id,
				name: takeoff.name,
				state: takeoff.state === "creating" ? "creating" : "active",
				updatedAt: String(takeoff.updatedAt),
			}))}
		/>
	);
};
